/**
 * Progressive disclosure online (WP-N2, docs/ONLINE-UI.md "What the book shows when"): the advanced parts of the
 * book and the hotkey row stay out of sight until they mean something to you — the first time you use them, or
 * when the first-evening ladder (onlineLadder.ts) reaches them. Once shown, a part stays shown (remembered per
 * character on this device), and something you are using right now (a freight run on, premises you hold, a
 * gang's turf) is never hidden. Pure; unit-tested in disclosure.test.ts.
 */
import { reached, type LadderFacts } from './onlineLadder';

export const FEATURES = ['freight', 'warn', 'jobs', 'events', 'markets', 'turf', 'premises', 'gangBiz', 'week', 'lenny', 'hideout'] as const;
export type Feature = (typeof FEATURES)[number];

/** How a part is named in the "New in your book" note (null: no note — it comes and goes with its use). */
export const FEATURE_NOTE: Record<Feature, { name: string; text: string } | null> = {
  freight: { name: 'Night Freight', text: 'A crew run from the pier: crates bought at Bonded Warehouse No. 3 and sold at the drop. The run sheet is under Freight on your book’s index (X).' },
  warn: null,
  jobs: { name: 'The firm’s jobs', text: 'Jobs for a crew with a role for each of you — the Savings Bank first: a wheelman, an inside man, a lookout. Plan it with your crew from your book’s index (U).' },
  events: { name: 'On tonight', text: 'Boats in at the quay, buyers in town, a rare motor, a contested yard: what’s on in the city tonight. It’s on your book’s index (O).' },
  markets: { name: 'The markets', text: 'Out-of-town buyers and the big buyers’ week, under Jobs in your book. They pay best for goods made in premises.' },
  turf: { name: 'Turf', text: 'Who runs which district, and contests for them, under Jobs in your book and on the gang ledger’s Turf tab.' },
  premises: { name: 'Premises', text: 'Lockups, warehouses and yards to make goods in and sell on. The premises sheet is on your book’s index (L).' },
  gangBiz: { name: 'The gang’s business', text: 'Premises bought and run from the treasury — and how the gang gets its first — on the gang ledger’s Business tab.' },
  hideout: { name: 'The hideout', text: 'Your gang’s home behind the door of 9 Pilcher Street, the safehouse: walk in, meet there, furnish it from the treasury. It’s on your book’s index: Hideout.' },
  week: { name: 'The week', text: 'Every week in Kessmouth has its own twist — a dockers’ strike, fog, the Flying Squad — and the week’s best firms are written down for ever. It’s on your book’s index: The week.' },
  lenny: { name: 'Lenny’s list', text: 'Three small errands a day from Lenny the Ear, only if you fancy them: each pays a little tip and standing with Lenny, and nothing happens if you leave them. They’re under Jobs, and on Lenny’s page in Contacts.' },
};

export interface DisclosureFacts {
  ladder: LadderFacts;
  inCrew: boolean;
  inGang: boolean;
  /** A freight run you're on (any status but completed). */
  freightOn: boolean;
  /** A firm's job planned for your crew or on (WP-X3). */
  jobOn?: boolean;
  /** Premises you hold or may run. */
  props: number;
  /** Your gang's businesses (premises the gang holds). */
  gangProps: number;
  /** A district your firm runs, or a contest your firm is in. */
  turfYours: boolean;
  /**
   * What the contact ladders have opened (WP-X1, shared/contacts.ts): Mags' first rung shows the markets, Lenny's
   * second the Turf pages to a character with no gang, his first Lenny's list (WP-X8). Absent = nothing yet.
   */
  access?: { markets?: boolean; turf?: boolean; lenny?: boolean };
}

/**
 * What shows now. `used` = parts opened before (or remembered as shown): they always show. A part in use right
 * now always shows, even if it never showed before.
 */
export function shownFeatures(f: DisclosureFacts, used: ReadonlySet<Feature>): Set<Feature> {
  const out = new Set<Feature>();
  const on = (k: Feature, why: boolean): void => {
    if (why || used.has(k)) out.add(k);
  };
  const L = f.ladder;
  on('freight', f.freightOn || f.inCrew || reached(L, 'freight'));
  // Warn the crew: only while a run is on (it does nothing otherwise) — never remembered.
  if (f.freightOn) out.add('warn');
  // The firm's jobs (WP-X3): a crew activity like freight — with the crew, or when the crew plans one.
  on('jobs', !!f.jobOn || f.inCrew || reached(L, 'freight'));
  on('events', reached(L, 'sid'));
  on('premises', f.props > 0 || f.inGang || reached(L, 'premises'));
  on('markets', f.props > 0 || f.gangProps > 0 || reached(L, 'premises') || !!f.access?.markets);
  on('turf', f.inGang || f.turfYours || reached(L, 'gang') || !!f.access?.turf);
  // The gang ledger's Business tab from the day you're in a gang: it is where a gang learns how to get premises.
  on('gangBiz', f.inGang);
  // The Kessmouth week (WP-X5): with the evening's events (they share the Evening Post), or once you run with others.
  on('week', reached(L, 'sid') || f.inCrew || f.inGang);
  // Lenny's list (WP-X8): after the first evening's first few steps (a car, a job, the bank, Sid's door), or as
  // soon as Lenny knows your name (his first rung). Light and optional: it never shows to a brand-new player.
  on('lenny', reached(L, 'sid') || !!f.access?.lenny);
  // The hideout (WP-X4): a gang's, so from the day you're in one (and it stays in the book after, as everything does).
  on('hideout', f.inGang);
  return out;
}

/** Parts that are shown now and were not before (the "New in your book" notes), in a stable order. */
export function newlyShown(now: ReadonlySet<Feature>, before: ReadonlySet<Feature>): Feature[] {
  return FEATURES.filter((k) => now.has(k) && !before.has(k) && FEATURE_NOTE[k] !== null);
}
